// Leave Management page and its dialogs. Excel files and the response e-mail stay Turkish.
const leaves = {
  page: {
    title: 'Leave Management',
    subtitle: 'View, approve and reject every employee’s leave requests in one place and report annual leave balances.',
    givenLeaves: 'Given Leaves',
    addLeave: 'Add Leave',
  },
  tabs: {
    label: 'Leave Management sections',
    requests: 'Requests',
    balance: 'Balance & Report',
    pendingCount_one: '{{count}} pending request',
    pendingCount_other: '{{count}} pending requests',
  },
  common: {
    days_one: '{{days}} day',
    days_other: '{{days}} days',
    refresh: 'Refresh',
    cancel: 'Cancel',
    save: 'Save',
  },
  stats: {
    share: '{{percent}}% of the total',
  },
  status: {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    unknown: 'Unknown',
  },
  requests: {
    tiles: {
      total: 'Total Requests',
      totalShort: 'Total',
      pending: 'Pending Requests',
      pendingShort: 'Pending',
      approved: 'Approved',
      approvedShort: 'Approved',
      rejected: 'Rejected',
      rejectedShort: 'Rejected',
    },
    search: 'Search by employee, e-mail or leave type…',
    searchShort: 'Search employee or leave type…',
    statusFilter: 'Status',
    filter: {
      all: 'All',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    listTitle: 'Leave Requests',
    empty: 'No leave requests to show.',
    rangeOf: 'of {{total}} requests',
    ofTotal: 'out of {{total}} in total',
    view: 'View the leave document',
    approve: 'Approve',
    reject: 'Reject',
    delete: 'Delete the leave request permanently',
    columns: {
      employee: 'Employee',
      email: 'E-mail',
      type: 'Leave Type',
      start: 'Start',
      end: 'End',
      duration: 'Duration',
      travelDays: 'Travel Time',
      status: 'Status',
      requestedAt: 'Requested',
      reason: 'Reason',
      address: 'Address',
      processedBy: 'Processed by',
      actions: 'Actions',
    },
  },
  balance: {
    totals: {
      entitlement: 'Total Entitlement (days)',
      entitlementShort: 'Entitlement',
      used: 'Used (days)',
      usedShort: 'Used',
      reserved: 'Pending (days)',
      reservedShort: 'Pending',
      remaining: 'Remaining (days)',
      remainingShort: 'Remaining',
    },
    search: 'Search by employee or department…',
    searchShort: 'Search employee or department…',
    year: 'Year',
    export: 'Export to Excel',
    listTitle: 'Annual Leave Balances — {{year}}',
    rangeOf: 'of {{total}} employees',
    share: '{{percent}}% of the total entitlement',
    empty: 'No employees to show.',
    usageTitle: 'Used {{used}} · Pending {{reserved}} · Entitlement {{total}} days',
    editEntitlement: 'Edit annual leave entitlement',
    history: 'View given leaves',
    columns: {
      employee: 'Employee',
      department: 'Department',
      entitlement: 'Entitlement',
      used: 'Used',
      reserved: 'Pending',
      remaining: 'Remaining',
      usage: 'Usage',
      actions: 'Actions',
    },
  },
  dialog: {
    title: 'Leave Document',
    start: 'Start',
    end: 'End',
    leaveDays: 'Leave/Report Duration',
    travelDays: 'Travel Time',
    requestedAt: 'Requested',
    file: 'Report File',
    openFile: 'View file',
    reason: 'Reason',
    address: 'Address',
    processedBy: 'Processed by',
    adminNote: 'Manager’s Note',
    delete: 'Delete',
    decideHint: 'The employee is notified by e-mail when you approve or reject. The note is optional.',
    rejectHint: 'Rejecting needs a reason; the note is sent to the employee by e-mail.',
    notePlaceholder: 'You can add an optional note…',
    notePlaceholderReject: 'State the reason for rejection (required)…',
    processing: 'Processing…',
  },
  delete: {
    title: 'Delete Leave Request',
    message: 'The {{type}} request of {{name}} will be deleted permanently.',
    warning: 'This cannot be undone. Do you want to continue?',
    confirm: 'Delete Permanently',
    deleting: 'Deleting…',
  },
  entitlement: {
    title: 'Annual Leave Entitlement',
    intro: 'Update the annual leave entitlement (days) of {{name}}.',
    field: 'Total Annual Leave Entitlement (days)',
    decrease: 'One day less',
    increase: 'One day more',
    used: 'Used',
    reserved: 'Pending',
    newRemaining: 'New remaining',
    note: 'The change is saved to the employee’s record and used for the balance of every year.',
    history: 'Given leaves',
    saving: 'Saving…',
  },
  add: {
    title: 'New Leave Entry',
    subtitle: 'Create leave for an employee — annual leave is deducted from that year’s balance automatically.',
    steps: {
      employee: 'Employee',
      employeeHint: 'Search for and select the employee.',
      type: 'Leave type',
      dates: 'Dates and duration',
      extra: 'Additional details',
      extraHint: 'All optional.',
    },
    employeeSearch: 'Search by employee or department…',
    employeesLoading: 'Loading employees…',
    noMatch: 'No matching employees',
    noDepartment: 'No department set',
    change: 'Change',
    balanceTitle: '{{year}} annual leave balance',
    balanceLoading: 'Calculating balance…',
    balancePlaceholder: 'The annual leave balance appears here once you select an employee.',
    remaining: 'Remaining',
    stats: {
      entitlement: 'Entitlement',
      used: 'Used',
      reserved: 'Pending',
      after: 'After this leave',
    },
    annualNote: 'Annual leave is deducted from the balance of the start date’s year.',
    notDeducted: 'This leave type is not deducted from the annual leave balance.',
    fields: {
      start: 'Start Date',
      end: 'End Date',
      leaveDays: 'Leave Duration (days)',
      leaveDaysHint: 'Saturday counts as 0.5 days, Sunday is not counted — calculated automatically.',
      travelDays: 'Travel Time (days)',
      reason: 'Reason',
      reasonPlaceholder: 'Optional description…',
      address: 'Address During Leave',
      addressPlaceholder: 'Where the employee will be during the leave',
    },
    holidays: {
      badge: 'Public Holiday',
      summary_one: 'There is {{count}} public holiday in the selected range — {{days}} days were not deducted.',
      summary_other: 'There are {{count}} public holidays in the selected range — {{days}} days were not deducted.',
      full: 'Full day',
      half: 'Half day',
    },
    insufficient: 'This leave exceeds the employee’s remaining {{year}} balance ({{remaining}} days). If you save it, the remaining balance will be {{after}} days.',
    file: {
      choose: 'Attach a report / document (optional)',
      hint: 'PDF, JPG, PNG or Word — click or drag and drop',
      remove: 'Remove',
    },
    approvedNote: 'The entry is added as approved. The window stays open after saving, so you can add another leave for the same employee.',
    save: 'Save Leave',
    saving: 'Saving…',
    toast: {
      employeesError: 'The employee list could not be loaded',
      recordsError: 'The employee’s leave records could not be loaded',
      noEmployee: 'Please select an employee',
      noDates: 'Enter the start and end dates',
      badRange: 'The end date cannot be before the start date',
      savedAnnual: '{{days}}-day leave saved for {{name}}. Remaining {{year}} balance: {{remaining}} days',
      savedOther: '{{type}} saved for {{name}}',
      saveError: 'The leave could not be saved',
    },
  },
  given: {
    title: 'Given Leaves',
    subtitle: 'Manually added leave and approved leave requests in one list.',
    subtitleEmployee: 'Manual and approved leave · Filter: {{name}}',
    search: 'Search by employee, department, leave type or reason…',
    filters: {
      year: 'Year',
      allYears: 'All years',
      type: 'Type',
      allTypes: 'All leave types',
      source: 'Source',
      allSources: 'All sources',
      employee: 'Employee',
      allEmployees: 'All employees',
    },
    source: {
      manual: 'Manual entry',
      request: 'Approved request',
    },
    totalsLabel: 'Summary of the filtered records',
    totals: {
      records: 'Records',
      employees: 'Employees',
      totalDays: 'Total Days',
      annualDays: 'Annual Leave Days',
      annualDaysShort: 'Annual leave',
      manual: 'Manual',
    },
    showing_one: 'Showing {{count}} record',
    showing_other: 'Showing {{count}} records',
    clearFilters: 'Clear Filters',
    loading: 'Loading leave records…',
    loadError: 'The given leave records could not be loaded.',
    empty: 'No leave records match these filters.',
    pickHint: 'Select a leave from the list to see its details.',
    columns: {
      employee: 'Employee',
      leave: 'Leave',
      dates: 'Dates',
      days: 'Days',
      source: 'Source',
    },
    detail: {
      start: 'Start',
      end: 'End',
      leaveDays: 'Leave Duration',
      travelDays: 'Travel Time',
      createdAt: 'Recorded',
      reason: 'Reason',
      address: 'Address',
      adminNote: 'Manager’s Note',
      processedBy: 'Processed by',
      openFile: 'Open Document',
      noFile: 'No document attached',
    },
  },
  celebration: {
    welcome: 'Welcome,',
    skip: 'Skip',
  },
  toast: {
    invalidDays: 'Enter a valid number of days',
    entitlementSaved_one: 'Annual leave entitlement of {{name}} updated to {{count}} day',
    entitlementSaved_other: 'Annual leave entitlement of {{name}} updated to {{count}} days',
    entitlementError: 'The annual leave entitlement could not be updated',
    reportError: 'The leave balance report could not be loaded',
    exportEmpty: 'There are no records to export',
    exported: 'Excel file downloaded',
    loadError: 'Leave requests could not be loaded',
    rejectNoteRequired: 'You must state a reason for rejection',
    approved: 'Leave request approved',
    rejected: 'Leave request rejected',
    actionError: 'The leave request could not be updated',
    deleted: 'Leave request deleted permanently',
    deleteError: 'The leave request could not be deleted',
  },
  types: {
    yillik: 'Annual Leave',
    mazeret: 'Personal Leave',
    raporlu: 'Sick Leave',
    dogum_oncesi: 'Prenatal Leave',
    babalik: 'Paternity Leave',
    evlilik: 'Marriage Leave',
    olum: 'Bereavement Leave',
    engelli_cocuk: 'Disabled Child Care Leave',
    dogum_sonrasi: 'Postnatal Leave',
    yatakli_tedavi: 'Inpatient Treatment',
    refakat: 'Caregiver Leave',
    sua: 'Radiation Leave',
    saglik_kurulu: 'Medical Board Report',
    ayakta_tedavi: 'Outpatient Treatment',
  },
};

export default leaves;
